import React, { useEffect, useMemo } from "react";
import {
    type AutofillData,
    usePersonalization,
    PersonalizationStatus,
} from "@foundation/personalization";

import { fetchExpandedStoriesFromApi } from "~api/client/fetchStoriesFromApi";
import ZoneModule from "~components/modules/zoneModule/zoneModule";
import type { ModuleRenderingProps } from "~components/shared/zones/utility";
import { modulesByIdFetched } from "~redux/features/modulesById-slice";
import { setPersonalization } from "~redux/features/personalization-slice";
import useRedux from "~redux/hooks";
import type { HydratedCurationModule, VariationComponent } from "~types/types";
import type { CuratedItem } from "~types/wardenclyffe";

interface PersonalizedModuleProps {
    module: VariationComponent;
    preview: boolean;
    renderingData: ModuleRenderingProps;
}

function PersonalizedModule({
    module,
    renderingData,
    preview,
}: PersonalizedModuleProps) {
    const { useAppSelector, useAppDispatch } = useRedux();
    const modules = useAppSelector((state) => state.modulesById);
    const dispatch = useAppDispatch();

    const { recommendations, status, error } = usePersonalization<CuratedItem>({
        autofillData: module.autofillData as AutofillData,
        transformResults: fetchExpandedStoriesFromApi,
    });

    useEffect(() => {
        if (error) {
            console.error(
                "Something went wrong fetching personalization data: ",
                error
            );
        }

        dispatch(
            setPersonalization({
                storiesKey: module.id,
                recommendations,
                status,
                error,
            })
        );
    }, [module.id, recommendations, status, error, dispatch]);

    // casting here because items is CuratedItem[] | CurationModule[] // ← invalid for HydratedCurationModule
    const moduleWithPersonalizedItems = useMemo(
        () => ({
            ...module,
            items: module.autofillData ? recommendations : module.items,
            autofillData: undefined,
        }),
        [module, recommendations]
    ) as HydratedCurationModule;

    useEffect(() => {
        if (recommendations.length) {
            dispatch(
                modulesByIdFetched({
                    ...modules,
                    [module.id]:
                        moduleWithPersonalizedItems as HydratedCurationModule,
                })
            );
        }
    }, [recommendations, dispatch, module.id, moduleWithPersonalizedItems]);

    const isFetching =
        module.autofillData && status !== PersonalizationStatus.Done;

    if (isFetching || (!isFetching && !recommendations.length)) {
        return null;
    }

    return (
        <ZoneModule
            module={moduleWithPersonalizedItems}
            renderingData={renderingData}
            preview={preview}
        />
    );
}

export default PersonalizedModule;
